// src/components/Auth/Auth.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MapPin, Mail, Lock, LogIn, ArrowLeft, Send } from "lucide-react";

import {
  signInUser,
  selectAuthLoading,
  selectAuthError,
  clearError,
} from "../../store/authSlice";
import { resetPassword } from "../../services/firebase";

/**
 * Auth component - login page with forgot password flow
 */
const Auth = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    dispatch(signInUser({ email, password }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError(null);
    setResetSending(true);

    try {
      await resetPassword(resetEmail);
      setResetSent(true);
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        setResetError("No account found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setResetError("Please enter a valid email address.");
      } else {
        setResetError("Failed to send reset email. Please try again.");
      }
    } finally {
      setResetSending(false);
    }
  };

  const enterForgotMode = () => {
    setForgotMode(true);
    setResetEmail(email);
    setResetSent(false);
    setResetError(null);
    dispatch(clearError());
  };

  const exitForgotMode = () => {
    setForgotMode(false);
    setResetSent(false);
    setResetError(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <MapPin className="text-secondary" size={40} />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Heat Map HQ</h1>
            <p className="text-sm text-gray-500">
              Location Intelligence Platform
            </p>
          </div>
        </div>

        {forgotMode ? (
          /* ---- FORGOT PASSWORD VIEW ---- */
          <>
            {resetSent ? (
              /* Success state */
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail size={24} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800 mb-2">
                  Check your email
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  We sent a password reset link to{" "}
                  <span className="font-medium text-gray-700">
                    {resetEmail}
                  </span>
                  . It may take a minute to arrive. If you don't see it, make
                  sure to check your spam folder.
                </p>
                <button
                  type="button"
                  onClick={exitForgotMode}
                  className="inline-flex items-center gap-2 text-sm text-secondary hover:text-secondary-dark font-medium transition"
                >
                  <ArrowLeft size={16} />
                  Back to sign in
                </button>
              </div>
            ) : (
              /* Reset form */
              <>
                <h2 className="text-lg font-semibold text-gray-800 mb-1">
                  Reset your password
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  Enter your email address and we will send you a link to reset
                  your password.
                </p>

                {resetError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {resetError}
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <Mail
                        size={18}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none transition"
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={resetSending}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {resetSending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Reset Link
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={exitForgotMode}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition"
                  >
                    <ArrowLeft size={14} />
                    Back to sign in
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          /* ---- LOGIN VIEW ---- */
          <>
            {/* Error message */}
            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Login form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none transition"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={enterForgotMode}
                    className="text-xs text-secondary hover:text-secondary-dark transition"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none transition"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn size={18} />
                    Sign In
                  </>
                )}
              </button>
            </form>

            {/* Dev mode notice */}
            {import.meta.env.VITE_DEV_MODE === "true" && (
              <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm text-center">
                <strong>Dev Mode:</strong> Auto-login enabled. Refresh to
                continue.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Auth;
